import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  inject,
  OnInit,
  QueryList,
  ViewChildren,
} from '@angular/core';
import { Database, onValue, ref } from '@angular/fire/database';
import {
  GoogleMapsModule,
  MapInfoWindow,
  MapMarker,
} from '@angular/google-maps';
import { User } from '@models';
import { Store } from '@ngrx/store';
import {
  fetchUsers,
  selectMapSelectedUserIds,
  selectUsers,
  setSelectedMapUsers,
} from '@state';

interface FirebaseDriver {
  id: string;
  location: { lat: number; lng: number };
  name: string;
}

@Component({
  selector: 'app-map',
  standalone: true,
  imports: [CommonModule, GoogleMapsModule],
  templateUrl: './map.page.html',
  styleUrl: './map.page.scss',
})
export class MapPage implements OnInit, AfterViewInit {
  @ViewChildren(MapInfoWindow) infoWindowsView:
    | QueryList<MapInfoWindow>
    | undefined;
  @ViewChildren(MapMarker) mapMarkers!: QueryList<MapMarker>;
  private database: Database = inject(Database);
  private markerMap = new Map<string, MapMarker>();
  private $selectedMapUsers = this.store.select(selectMapSelectedUserIds);

  public center = { lat: -26.2041, lng: 28.0473 }; // Center on Johannesburg
  public zoom = 5;
  public drivers: FirebaseDriver[] = [];
  public selectedDriverIds: string[] = [];
  public motorbikeIcon = {
    url: 'bike_icon.png',
    scaledSize: { width: 32, height: 32, equals: () => false },
  };

  private allFirebaseDrivers: FirebaseDriver[] = [];
  private users: User[] = [];

  constructor(private store: Store) {
    const dbRef = ref(this.database, 'drivers');
    onValue(dbRef, (snapshot) => {
      const drivers: FirebaseDriver[] = [];
      snapshot.forEach((child) => {
        const driver = child.val();
        drivers.push({
          id: child.key,
          location: {
            lat: driver.location.latitude,
            lng: driver.location.longitude,
          },
          name: driver?.firstName + driver?.lastName || '--', // Replace with actual driver name
        });
      });
      this.allFirebaseDrivers = drivers;
      this.filterFirebaseDrivers();
      setTimeout(() => {
        this.openSelectedInfoWindows();
      });
    });
  }

  ngOnInit() {
    this.store.dispatch(fetchUsers());

    this.$selectedMapUsers.subscribe((selectedMapUsers) => {
      if (selectedMapUsers) {
        this.selectedDriverIds = selectedMapUsers;
        this.openSelectedInfoWindows();
      }
    });

    this.store.select(selectUsers).subscribe((users) => {
      this.users = users.filter((user) => user.role == 'user');
      this.filterFirebaseDrivers();
    });
  }

  private filterFirebaseDrivers() {
    this.drivers = this.allFirebaseDrivers.filter((driver) => {
      return this.users.find((u) => u.id === driver.id);
    });
  }

  ngAfterViewInit() {
    this.mapMarkers.changes.subscribe(() => {
      this.registerMarkers();
      this.openSelectedInfoWindows();
    });
  }

  private registerMarkers() {
    this.mapMarkers.forEach((marker) => {
      const userId = marker.getTitle();
      if (userId) {
        this.markerMap.set(userId, marker);
      }
    });
  }

  private openSelectedInfoWindows() {
    this.selectedDriverIds.forEach((userId) => {
      const marker = this.getMarkerByDriverId(userId);
      const infoWindow = this.getInfoWindowByDriverId(userId);
      if (marker && infoWindow) {
        infoWindow.open(marker);
      }
    });
  }

  private getMarkerByDriverId(userId: string): MapMarker | undefined {
    return this.markerMap.get(userId);
  }

  private getInfoWindowByDriverId(userId: string): MapInfoWindow | undefined {
    const index = this.selectedDriverIds.indexOf(userId);
    return this.infoWindowsView?.toArray()[index];
  }

  public onMarkerClick(userId: string) {
    this.selectedDriverIds = [...this.selectedDriverIds, userId];
    this.store.dispatch(setSelectedMapUsers({ users: this.selectedDriverIds }));
  }

  public onInfoWindowClose(userId: string) {
    this.selectedDriverIds = this.selectedDriverIds.filter(
      (_userId) => _userId !== userId
    );
    this.store.dispatch(setSelectedMapUsers({ users: this.selectedDriverIds }));
  }

  public getDriverLocation(userId: string): google.maps.LatLngLiteral {
    const driver = this.drivers.find((d) => d.id === userId);
    if (!driver) return { lat: 0, lng: 0 };
    driver.location.lat = Math.round(driver.location.lat * 100000) / 100000;
    driver.location.lng = Math.round(driver.location.lng * 100000) / 100000;
    return driver.location;
  }

  public getDriverName(userId: string): string {
    const driver = this.drivers.find((d) => d.id === userId);
    return driver ? driver.name : '';
  }
}
